import { createRouter, createWebHistory } from "vue-router";

// index.html carries the <title> that crawlers index and that og:title advertises.
// Read it before afterEach starts overwriting it, so the runtime title cannot drift
// from what the page claims to be — it had drifted to "Revolutionizing Reviews",
// which carries no keyword a buyer would ever search for.
const DEFAULT_TITLE =
  (typeof document !== "undefined" && document.title) ||
  "Review My eLearning | eLearning Course Review Tool";

// An SPA serves every route from one document, so without this every page reports
// the same URL to a crawler. Rebuilt per navigation rather than written once.
function setCanonical(path) {
  if (typeof document === "undefined") return;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", window.location.origin + path);
}

const Portfolio = () => import("./layouts/Portfolio.vue");
import DynamicRouteComponent from "./layouts/DynamicRouteComponent.vue";
const Main = () => import("./pages/Main.vue");
const ReviewPage = () => import("./pages/ReviewPage.vue");
// The redesigned pages live beside the current ones; helpers/uiVersion.js picks.
import { isNewUi } from "./helpers/uiVersion.js";
const CourseList = () =>
  isNewUi() ? import("./layouts/next/CourseListNext.vue") : import("./layouts/CourseList.vue");
const CourseDetails = () =>
  isNewUi()
    ? import("./layouts/next/CourseDetailsNext.vue")
    : import("./layouts/CourseDetails.vue");
const Login = () => import("./layouts/Login.vue");
const Signup = () => import("./layouts/Signup.vue");
const Success = () => import("./layouts/Success.vue");
const Profile = () => import("./layouts/UserProfile.vue");
const OwnershipTransfer = () => import("./layouts/OwnershipTransfer.vue");
const EmailChange = () => import("./layouts/EmailChange.vue");
const Pricing = () => import("./layouts/Pricing.vue");
const ResetPassword = () => import("./layouts/ResetPassword.vue");
const ForgotPassword = () => import("./layouts/ForgotPassword.vue");
const ResendConfirmation = () => import("./layouts/ResendConfirmation.vue");
const Unsubscribe = () => import("./layouts/Unsubscribe.vue");
const NotFound = () => import("./layouts/NotFound.vue");
const SupportLogin = () => import("./layouts/SupportLogin.vue");
const OAuthConsent = () => import("./layouts/OAuthConsent.vue");
const Terms = () => import("./layouts/Terms.vue");
const Privacy = () => import("./layouts/Privacy.vue");
const Support = () => import("./layouts/Support.vue");
const DeveloperDoc = () => import("./layouts/DeveloperDoc.vue");
const Review = () => import("./layouts/Review.vue");
const ReviewPublic = () => import("./layouts/ReviewPublic.vue");
const Features = () => import("./layouts/Features.vue");

import { useUserStore } from "./stores/userStore.js";
import {
  normalizeAuthRedirect,
  rememberAuthRedirect,
} from "./helpers/authRedirect";
import { storeEmailChangeToken } from "./helpers/emailChangeToken";
import {
  storeOwnershipTransferToken,
  storePasswordResetToken,
} from "./helpers/sensitiveAccountTokens";
import { mergeRouteQuery } from "./helpers/navigationQuery";
import { capturePromoFromRoute } from "./helpers/promoCode";
import { captureAttributionFromRoute } from "./helpers/attribution";

let host = window.location.host;
let subdomain = host.split(".")[0];
const routes = [
  {
    path: "/",
    component: Main,
    name: "Home",
    meta: {
      requiresAuth: false,
    },
    children: [
      {
        path: "/",
        component: DynamicRouteComponent,
        name: "Dynamic",
        meta: {
          requiresAuth: false,
          title: DEFAULT_TITLE,
        },
      },
      {
        path: "/features",
        component: Features,
        name: "Features",
        meta: {
          requiresAuth: false,
          title: "Review My eLearning - Features",
        },
      },
      {
        path: "/portfolio",
        component: Portfolio, // replace with your actual component
        name: "Org Portfolio",
        meta: {
          requiresAuth: false,
          title: subdomain.toUpperCase() + " - Portfolio",
          ignoreTheme: false,
        },
      },
      {
        path: "/courses",
        component: CourseList,
        name: "Courses",
        meta: {
          requiresAuth: true,

          title: "Review My eLearning - Courses",
        },
      },
      {
        path: "/course/:id",
        component: CourseDetails,
        name: "CourseDetails",
        meta: {
          requiresAuth: true,

          title: "Review My eLearning - Course Details",
        },
      },
      {
        path: "/pricing",
        component: Pricing,
        name: "Pricing",
        meta: {
          requiresAuth: false,

          title: "Review My eLearning - Pricing",
          ignoreTheme: true,
        },
      },
      {
        path: "/cancel",
        component: Pricing,
        name: "Cancel",
        meta: {
          requiresAuth: false,

          title: "Review My eLearning - Cancel",
        },
      },
      {
        path: "/login",
        component: Login,
        name: "Login",
        meta: {
          requiresAuth: false,
          title: "Review My eLearning - Login",
        },
      },
      {
        path: "/signup",
        component: Signup,
        name: "Signup",
        meta: {
          requiresAuth: false,
          title: "Review My eLearning - Create Your Account",
        },
      },
      {
        path: "/support_sign_in",
        component: SupportLogin,
        name: "Support Login",
        meta: {
          requiresAuth: false,

          title: "Review My eLearning - Support Login",
        },
      },
      {
        path: "/oauth/consent",
        component: OAuthConsent,
        name: "OAuth Consent",
        meta: {
          requiresAuth: true,
          title: "Review My eLearning - Authorize Application",
        },
      },
      {
        path: "/success/:sessionToken",
        component: Success,
        name: "Success",
        meta: {
          requiresAuth: false,
          title: "Review My eLearning - Success",
        },
      },
      {
        path: "/profile",
        component: Profile,
        name: "Profile",
        meta: {
          requiresAuth: true,

          title: "Review My eLearning - Profile",
        },
      },
      {
        path: "/ownership-transfer",
        component: OwnershipTransfer,
        name: "Ownership Transfer",
        meta: {
          requiresAuth: true,
          title: "Review My eLearning - Ownership Transfer",
        },
      },
      {
        path: "/email-change",
        component: EmailChange,
        name: "Confirm Email Change",
        meta: {
          requiresAuth: false,
          title: "Review My eLearning - Confirm Email Change",
        },
      },
      {
        path: "/reset-password",
        component: ResetPassword,
        name: "Reset Password",
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: "/reset-password/:token",
        component: ResetPassword,
        name: "Reset Password Legacy Link",
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: "/forgot-password/",
        component: ForgotPassword,
        name: "Forgot Password",
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: "/resend-confirmation/",
        component: ResendConfirmation,
        name: "Resend Confirmation",
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: "/join",
        component: Pricing,
        name: "Join",
        meta: {
          requiresAuth: false,

          title: "Review My eLearning - Join",
          ignoreTheme: true,
        },
      },
      {
        path: "/join/:promo",
        component: Pricing,
        name: "Promo",
        meta: {
          requiresAuth: false,

          title: "Review My eLearning - Promo",
          ignoreTheme: true,
        },
      },

      {
        path: "/unsubscribe/",
        component: Unsubscribe,
        name: "Unsubscribe",
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: "/terms/",
        component: Terms,
        name: "Terms",
        meta: {
          requiresAuth: false,

          title: "Review My eLearning - Terms of Service",
        },
      },
      {
        path: "/privacy/",
        component: Privacy,
        name: "Privacy",
        meta: {
          requiresAuth: false,

          title: "Review My eLearning - Privacy Policy",
        },
      },
      {
        path: "/support",
        component: Support,
        name: "Support",
        meta: {
          // Public: the page is a signpost (knowledge base, developer docs) and
          // only the ticket form needs an account — it gates itself. Gating the
          // whole page sent logged-out visitors to the knowledge base instead,
          // which also made /docs/mcp and /docs/course-ingest-api unreachable,
          // since this is the only page linking to them.
          requiresAuth: false,
          title: "Review My eLearning - Support",
        },
      },
      {
        path: "/docs/:slug",
        component: DeveloperDoc,
        name: "DeveloperDoc",
        meta: {
          requiresAuth: false,
          title: "Review My eLearning - Developer Documentation",
        },
      },

      {
        path: "/:pathMatch(.*)*", // catch all 404 - must be last!
        name: "NotFound",
        component: NotFound,
        meta: {
          requiresAuth: false,
        },
      },
    ],
  },
  {
    path: "/",
    component: ReviewPage,
    name: "ReviewPage",
    meta: {
      requiresAuth: false,
      title: DEFAULT_TITLE,
    },
    children: [
      {
        path: "/review/:rid",
        component: Review,
        name: "Review",
        meta: {
          requiresAuth: false,
          hideNav: true,
          title: "Review My eLearning - Review",
        },
      },
      {
        path: "/view/:rid",
        component: Review,
        name: "View",
        meta: {
          requiresAuth: false,
          hideNav: true,
          title: "Review My eLearning - View",
        },
      },
      {
        path: "/comment/:cid",
        component: Review,
        name: "View Comment",
        meta: {
          requiresAuth: true,
          hideNav: true,
          title: "Review My eLearning - View Comment",
        },
      },
      {
        path: "/public-review/:rid",
        component: ReviewPublic,
        name: "Public Review",
        meta: {
          requiresAuth: false,
          hideNav: true,
          title: "Review My eLearning - Public Review",
        },
      },
      {
        path: "/public-course/:cid",
        component: ReviewPublic,
        name: "Public Course",
        meta: {
          requiresAuth: false,
          hideNav: true,
          title: "Review My eLearning - Public Course",
        },
      },
      {
        path: "/course-review/:cid",
        component: ReviewPublic,
        name: "Course Review",
        meta: {
          requiresAuth: false,
          hideNav: true,
          title: "Review My eLearning - Course Review",
        },
      },
    ],
  },
];
const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: () => ({ top: 0 }),
  routes: routes,
});

router.beforeEach((to, from, next) => {
  // This also covers an in-app navigation to an emailed link. Navigation
  // guards run before the new URL is committed, so analytics never sees the
  // capability fragment.
  if (to.path === "/email-change" && to.hash) {
    const fragment = to.hash.startsWith("#") ? to.hash.slice(1) : to.hash;
    storeEmailChangeToken(new URLSearchParams(fragment).get("token"));
    next({ path: to.path, query: to.query, hash: "", replace: true });
    return;
  }
  if (to.path === "/ownership-transfer" && to.hash) {
    const fragment = to.hash.startsWith("#") ? to.hash.slice(1) : to.hash;
    storeOwnershipTransferToken(new URLSearchParams(fragment).get("token"));
    next({ path: to.path, query: to.query, hash: "", replace: true });
    return;
  }
  if (to.path === "/reset-password" && to.hash) {
    const fragment = to.hash.startsWith("#") ? to.hash.slice(1) : to.hash;
    storePasswordResetToken(new URLSearchParams(fragment).get("token"));
    next({ path: to.path, query: to.query, hash: "", replace: true });
    return;
  }
  if (to.name === "Reset Password Legacy Link") {
    storePasswordResetToken(to.params.token);
    next({ path: "/reset-password", query: to.query, replace: true });
    return;
  }

  const userStore = useUserStore();

  // Capture a promo code from wherever it arrives — /join/CODE, ?promo=CODE on the
  // homepage, or a link from an external landing page — before mergeRouteQuery strips
  // it. `promo` is a NAVIGATION_SCOPED_QUERY_KEY, so it does not survive the next
  // navigation in the URL; persisting it here is what lets a campaign send traffic to
  // the homepage instead of dropping strangers onto /pricing.
  capturePromoFromRoute(to);
  // utm_* survive navigation in the URL already, but not a full page load — and
  // signup, email confirmation and the Stripe round trip are all full page loads.
  // Persisting them here is what lets a conversion name the campaign that caused it.
  captureAttributionFromRoute(to);

  const mergedQuery = mergeRouteQuery(to.query, from.query);

  // Only login and collaborator signup may carry an explicit, validated
  // return path. Never inherit one from a previous route or copy a sensitive
  // ownership fragment into a query string.
  const explicitAuthRedirect = normalizeAuthRedirect(to.query.redirect);
  if (
    !["/login", "/signup"].includes(to.path) ||
    !explicitAuthRedirect
  ) {
    delete mergedQuery.redirect;
  } else {
    mergedQuery.redirect = explicitAuthRedirect;
  }

  // Store referrer when navigating to support page
  if (to.path === "/support" && from.path !== "/support" && from.path !== "/") {
    sessionStorage.setItem("supportReferrer", window.location.origin + from.fullPath);
  }

  if (to && to.meta && to.meta.requiresAuth) {
    if (userStore.user.email) {
      if (!isEqual(to.query, mergedQuery)) {
        next({ ...to, query: mergedQuery });
      } else {
        next();
      }
    } else {
      // Keep invitation fragments in module memory while the user signs in;
      // never copy them into a query string, history entry, or server log.
      rememberAuthRedirect(to.fullPath);
      next("/login");
    }
  } else {
    if (to.path === "/" && userStore.user.email) {
      next("/courses"); // Redirect to /courses if the user is authenticated and navigates to /
    } else if (!isEqual(to.query, mergedQuery)) {
      next({ ...to, query: mergedQuery });
    } else {
      next();
    }
  }
});
router.afterEach((to, from, next) => {
  document.title = (to && to.meta && to.meta.title) || DEFAULT_TITLE;
  if (to && to.path) setCanonical(to.path);
  // Note: Page views are tracked in PostHog, not Customer.io
  // Customer.io is used for behavioral events that drive messaging
});

function isEqual(obj1, obj2) {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
}

export default router;
